import React from "react";

function SummaryRow({ label, value, highlight, mono }) {
  return (
    <div className="summary-row">
      <span className="summary-label">{label}</span>
      <span className={`summary-value ${highlight ? "summary-value--highlight" : ""} ${mono ? "summary-value--mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function BookingSummary({ flight, passenger, confirmed, onConfirm, onReset }) {
  const taxes = flight ? Math.round(flight.price * 0.18) : 0;
  const total = flight ? flight.price + taxes : 0;
  const bookingRef = confirmed
    ? `SKY${Math.random().toString(36).substring(2, 8).toUpperCase()}`
    : null;

  if (confirmed && flight) {
    return (
      <div className="summary-card summary-card--confirmed">
        <div className="confirmed-header">
          <div className="confirmed-check">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div>
            <div className="confirmed-title">Booking Confirmed</div>
            <div className="confirmed-ref">Ref: {bookingRef}</div>
          </div>
        </div>

        <div className="confirmed-route">
          <div className="conf-city">
            <div className="conf-code">{flight.fromCode}</div>
            <div className="conf-name">{flight.from}</div>
          </div>
          <div className="conf-line">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <div className="conf-city">
            <div className="conf-code">{flight.toCode}</div>
            <div className="conf-name">{flight.to}</div>
          </div>
        </div>

        <div className="summary-rows">
          <SummaryRow label="Passenger" value={passenger.name} />
          <SummaryRow label="Flight" value={`${flight.airline} · ${flight.code}`} />
          <SummaryRow label="Departure" value={`${flight.departure} → ${flight.arrival}`} />
          <SummaryRow label="Duration" value={flight.duration} />
          <SummaryRow label="Class" value={flight.class} />
          <div className="summary-divider" />
          <SummaryRow label="Base Fare" value={`₹${flight.price.toLocaleString()}`} mono />
          <SummaryRow label="Taxes & Fees (18%)" value={`₹${taxes.toLocaleString()}`} mono />
          <div className="summary-divider" />
          <SummaryRow label="Total Paid" value={`₹${total.toLocaleString()}`} highlight mono />
        </div>

        <button className="reset-btn" onClick={onReset}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
          </svg>
          Book Another Flight
        </button>
      </div>
    );
  }

  return (
    <div className={`summary-card ${!flight ? "summary-card--empty" : ""}`}>
      <div className="card-header">
        <div className="card-header-left">
          <div className="card-icon card-icon--purple">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
              <rect x="9" y="3" width="6" height="4" rx="1" ry="1"/>
            </svg>
          </div>
          <div>
            <div className="card-title">Booking Summary</div>
            <div className="card-sub">{flight ? "Review and confirm" : "Select a flight to begin"}</div>
          </div>
        </div>
      </div>

      {!flight ? (
        <div className="summary-empty">
          <div className="empty-plane">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19 4c-2 0-4 1-3.5 2.5L12 10 4.8 6.2c-.5-.3-1.1 0-1.2.5L3 8c-.1.5.2 1 .7 1.2L9 11.5 6.5 14H4l-1 2 2 1 1-2h2.5L11.5 17.5l-2.5.8c-.4.1-.7.6-.7 1.1l.2 1.2c.1.5.7.8 1.2.5L19 16l-.2 3.2z"/>
            </svg>
          </div>
          <p className="summary-empty-text">Select a flight from the list to see your booking summary</p>
        </div>
      ) : (
        <>
          {/* Flight Route Visual */}
          <div className="summary-route">
            <div className="sr-point">
              <div className="sr-code">{flight.fromCode}</div>
              <div className="sr-city">{flight.from}</div>
              <div className="sr-time">{flight.departure}</div>
            </div>
            <div className="sr-mid">
              <div className="sr-airline-chip">{flight.airline}</div>
              <div className="sr-duration">{flight.duration}</div>
            </div>
            <div className="sr-point sr-point--right">
              <div className="sr-code">{flight.toCode}</div>
              <div className="sr-city">{flight.to}</div>
              <div className="sr-time">{flight.arrival}</div>
            </div>
          </div>

          <div className="summary-rows">
            <SummaryRow label="Passenger" value={passenger.name || <span className="missing">Not entered</span>} />
            {passenger.email && <SummaryRow label="Email" value={passenger.email} />}
            <SummaryRow label="Flight No." value={flight.code} mono />
            <SummaryRow label="Duration" value={flight.duration} />
            <SummaryRow label="Class" value={flight.class} />
            <SummaryRow label="Seats Left" value={`${flight.seats} available`} />
            <div className="summary-divider" />
            <SummaryRow label="Base Fare" value={`₹${flight.price.toLocaleString()}`} mono />
            <SummaryRow label="Taxes & Fees (18%)" value={`₹${taxes.toLocaleString()}`} mono />
            <div className="summary-divider" />
            <SummaryRow label="Total" value={`₹${total.toLocaleString()}`} highlight mono />
          </div>

          <button
            className="confirm-btn"
            onClick={onConfirm}
            disabled={!passenger.name.trim()}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19 4c-2 0-4 1-3.5 2.5L12 10 4.8 6.2c-.5-.3-1.1 0-1.2.5L3 8c-.1.5.2 1 .7 1.2L9 11.5 6.5 14H4l-1 2 2 1 1-2h2.5L11.5 17.5l-2.5.8c-.4.1-.7.6-.7 1.1l.2 1.2c.1.5.7.8 1.2.5L19 16l-.2 3.2z"/>
            </svg>
            Confirm Booking · ₹{total.toLocaleString()}
          </button>

          {!passenger.name.trim() && (
            <p className="confirm-hint">Enter passenger name to confirm</p>
          )}

          <button className="ghost-btn" onClick={onReset}>Clear selection</button>
        </>
      )}
    </div>
  );
}

export default BookingSummary;